import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useParams } from 'react-router-dom';
import { 
  ArrowLeftIcon,
  DocumentTextIcon,
  UserIcon,
  ClockIcon
} from '@heroicons/react/24/outline';
import { useAuthContext } from '../contexts/AuthContext';
import { usePageTitle } from '../hooks/usePageTitle';
import { messageService, Conversation } from '../services/messageService';
import { userService, User } from '../services/userService';
import { businessService } from '../services/businessService';

const AdminConversationView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { currentUser, userProfile } = useAuthContext();
  const [conversation, setConversation] = useState<Conversation | null>(null);
  const [users, setUsers] = useState<User[]>([]);
  const [businessSeoUrl, setBusinessSeoUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  usePageTitle(conversation ? `Conversation: ${conversation.businessTitle} | Admin` : 'Conversation | Admin');

  useEffect(() => {
    const loadConversation = async () => {
      if (!id) return;
      
      try {
        setLoading(true);
        // For now, we'll get all conversations and find the one we need
        // In a real app, you'd have a getConversationById method
        const allConversations = await messageService.getAllConversations();
        const foundConversation = allConversations.find(c => c.id === id);
        
        if (foundConversation) {
          setConversation(foundConversation);
          
          // Load user data for participant names
          const allUsers = await userService.getAllUsers();
          setUsers(allUsers);
          
          // Load business to get SEO URL
          if (foundConversation.businessId) {
            try {
              const business = await businessService.getBusiness(foundConversation.businessId);
              if (business?.basic.seoUrl) {
                setBusinessSeoUrl(business.basic.seoUrl);
              }
            } catch (err) {
              // Ignore errors, will fall back to ID
              console.error('Error loading business for SEO URL:', err);
            }
          }
        } else {
          setError('Conversation not found');
        }
      } catch (error) {
        console.error('Error loading conversation:', error);
        setError('Failed to load conversation');
      } finally {
        setLoading(false);
      }
    };

    loadConversation();
  }, [id]);

  // Check if user is admin
  const isAdmin = currentUser && userProfile?.profile.userType === 'admin';

  const getUserName = (userId: string) => {
    const user = users.find(u => u.id === userId);
    if (user) {
      return `${user.profile.firstName} ${user.profile.lastName}`;
    }
    return userId; // Fallback to ID if user not found
  };

  const getTimeAgo = (timestamp: any) => {
    if (!timestamp) return 'Unknown time';
    
    const now = new Date();
    const messageTime = timestamp.toDate();
    const diffInHours = Math.floor((now.getTime() - messageTime.getTime()) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return 'Just now';
    if (diffInHours < 24) return `${diffInHours}h ago`;
    if (diffInHours < 168) return `${Math.floor(diffInHours / 24)}d ago`;
    return `${Math.floor(diffInHours / 168)}w ago`;
  };

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-600 text-lg mb-4">⚠️ Access Denied</div>
          <p className="text-gray-600 mb-4">You don't have permission to access the admin area.</p>
          <Link to="/" className="bg-primary-600 text-white px-4 py-2 rounded hover:bg-primary-700">
            Go Home
          </Link>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading conversation...</p>
        </div>
      </div>
    );
  }

  if (error || !conversation) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-600 text-lg mb-4">❌ Error</div>
          <p className="text-gray-600 mb-4">{error || 'Conversation not found'}</p>
          <Link to="/admin/messages" className="bg-primary-600 text-white px-4 py-2 rounded hover:bg-primary-700">
            Back to Messages
          </Link>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Conversation: {conversation.businessTitle} | Admin</title>
        <meta name="description" content="Conversation details in admin dashboard" />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <div className="bg-white shadow-sm border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <Link
                  to="/admin/messages"
                  className="flex items-center text-gray-600 hover:text-gray-900"
                >
                  <ArrowLeftIcon className="h-5 w-5 mr-2" />
                  Back to Messages
                </Link>
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">Conversation Details</h1>
                  <p className="text-gray-600 mt-1">View conversation information and messages</p>
                </div>
              </div>
              <div className="flex items-center space-x-4">
                <Link
                  to="/admin/dashboard"
                  className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-md font-medium transition-colors"
                >
                  Back to Dashboard
                </Link>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="space-y-6">
            {/* Conversation Header */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center space-x-4 mb-4">
                <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center">
                  <DocumentTextIcon className="h-6 w-6 text-primary-600" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900">
                    {conversation.businessTitle || 'Business Conversation'}
                  </h2>
                  <p className="text-gray-600">Business ID: {conversation.businessId}</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-sm font-medium text-gray-700">Participants</p>
                  <p className="text-lg font-semibold text-gray-900">{conversation.participants.length}</p>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-sm font-medium text-gray-700">Total Messages</p>
                  <p className="text-lg font-semibold text-gray-900">{conversation.messageCount || 0}</p>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-sm font-medium text-gray-700">Last Activity</p>
                  <p className="text-lg font-semibold text-gray-900">
                    {conversation.lastMessage ? getTimeAgo(conversation.lastMessage.timestamp) : 'No activity'}
                  </p>
                </div>
              </div>
            </div>

            {/* Participants */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Participants</h3>
              <div className="space-y-3">
                {conversation.participants.map((participantId, index) => (
                  <div key={index} className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                    <UserIcon className="h-5 w-5 text-gray-400" />
                    <span className="text-gray-900 font-medium">{getUserName(participantId)}</span>
                    <span className="text-gray-500 text-sm">({participantId})</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Last Message Preview */}
            {conversation.lastMessage && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Last Message</h3>
                <div className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium text-gray-900">
                      {getUserName(conversation.lastMessage.senderId)}
                    </span>
                    <div className="flex items-center space-x-2 text-gray-500">
                      <ClockIcon className="h-4 w-4" />
                      <span className="text-sm">{getTimeAgo(conversation.lastMessage.timestamp)}</span>
                    </div>
                  </div>
                  <p className="text-gray-700">{conversation.lastMessage.content}</p>
                </div>
              </div>
            )}

            {/* Inquiry Details - Show if this is an inquiry conversation */}
            {conversation.lastMessage && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Inquiry Details</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <p className="text-sm font-medium text-gray-700">From</p>
                    <p className="text-gray-900">{getUserName(conversation.lastMessage.senderId)}</p>
                  </div>
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <p className="text-sm font-medium text-gray-700">Business</p>
                    <p className="text-gray-900">{conversation.businessTitle || 'Unknown Business'}</p>
                  </div>
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <p className="text-sm font-medium text-gray-700">Message</p>
                    <p className="text-gray-900">{conversation.lastMessage.content}</p>
                  </div>
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <p className="text-sm font-medium text-gray-700">Status</p>
                    <p className="text-gray-900 capitalize">{conversation.status}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Conversation Actions */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Actions</h3>
              <div className="flex items-center space-x-4">
                <Link
                  to={businessSeoUrl ? `/business/${businessSeoUrl}` : `/business/${conversation.businessId}`}
                  className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-md font-medium transition-colors"
                >
                  View Business Listing
                </Link>
                <button className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md font-medium transition-colors">
                  Archive Conversation
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AdminConversationView; 